import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, Field, PercentInput, QueryStatus, useToast } from "@cx/ui";
import type { CompanyFiscalProfile, TaxWizardResult } from "@cx/contracts";
import {
  BUSINESS_BRANCHES,
  REVENUE_BANDS,
  TAX_REGIMES,
  WIZARD_ACTIVITIES,
  type BusinessBranch,
  type RevenueBand,
  type TaxRegime,
  type WizardActivity,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { CompanyFiscalCard } from "./TaxOperationsPage";
import { ACTIVITY_LABEL, BRANCH_LABEL, CRT_LABEL, REVENUE_BAND_LABEL, TAX_REGIME_LABEL } from "./tax-labels";

type Form = {
  regime: TaxRegime | "";
  revenueBand: RevenueBand | "";
  branch: BusinessBranch | "";
  activities: WizardActivity[];
  creditSnPct4: number | null;
};

/**
 * Assistente de naturezas (RF-VEN-04): regime, faixa de faturamento, ramo e atividades → CRT da empresa e naturezas de
 * operação com regras sugeridas. Não altera naturezas que já existem com o mesmo nome.
 */
export function TaxWizardPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const profile = useQuery({
    queryKey: ["fiscal-company", companyId],
    queryFn: () => api<CompanyFiscalProfile>("/fiscal/company"),
  });
  const [f, setF] = useState<Form>({
    regime: "",
    revenueBand: "",
    branch: "",
    activities: ["compra_venda"],
    creditSnPct4: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    setF((x) => ({
      ...x,
      regime: x.regime || p.taxRegime || "",
      revenueBand: x.revenueBand || p.revenueBand || "",
      branch: x.branch || p.businessBranch || "",
    }));
  }, [profile.data]);
  const run = useMutation({
    mutationFn: () =>
      api<TaxWizardResult>("/tax-operations/wizard", {
        method: "POST",
        body: JSON.stringify({
          regime: f.regime || undefined,
          revenueBand: f.revenueBand || undefined,
          branch: f.branch || undefined,
          activities: f.activities,
          creditSnPct4: f.creditSnPct4,
        }),
      }),
    onSuccess: (r) => {
      setErrors({});
      void qc.invalidateQueries({ queryKey: ["tax-operations", companyId] });
      void qc.invalidateQueries({ queryKey: ["fiscal-company", companyId] });
      toast("success", `${r.created.length} natureza(s) criada(s)`);
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });
  const toggle = (a: WizardActivity, on: boolean) =>
    setF((x) => ({ ...x, activities: on ? [...x.activities, a] : x.activities.filter((y) => y !== a) }));
  const simples = f.regime === "simples" || f.regime === "mei";
  const r = run.data;
  return (
    <>
      <ContentHeader
        title="Assistente de naturezas de operação"
        breadcrumb={["Preferências", "Fiscal", "Naturezas de operação", "Assistente"]}
      />
      <CompanyFiscalCard canEdit={can(perms, "companies", "update")} />
      <QueryStatus loading={profile.isLoading} error={profile.error} />
      <section className="box card mb-3" aria-labelledby="tax-wizard">
        <div className="card-body">
          <h2 id="tax-wizard" className="h5">
            Como a empresa opera
          </h2>
          <p className="text-muted small">
            As naturezas criadas trazem regras sugeridas (CFOP, CST/CSOSN, PIS/COFINS e IBS/CBS). Revise-as
            com o seu contador antes de emitir notas.
          </p>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate();
            }}
          >
            <Field label="Regime tributário" required className="col-md-4" error={errors.regime}>
              <select
                className="form-select"
                value={f.regime}
                onChange={(e) => setF({ ...f, regime: e.target.value as TaxRegime | "" })}
              >
                <option value="">Selecione</option>
                {TAX_REGIMES.map((x) => (
                  <option key={x} value={x}>
                    {TAX_REGIME_LABEL[x]}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Faturamento dos últimos 12 meses"
              required
              className="col-md-4"
              error={errors.revenueBand}
            >
              <select
                className="form-select"
                value={f.revenueBand}
                onChange={(e) => setF({ ...f, revenueBand: e.target.value as RevenueBand | "" })}
              >
                <option value="">Selecione</option>
                {REVENUE_BANDS.map((x) => (
                  <option key={x} value={x}>
                    {REVENUE_BAND_LABEL[x]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ramo" required className="col-md-4" error={errors.branch}>
              <select
                className="form-select"
                value={f.branch}
                onChange={(e) => setF({ ...f, branch: e.target.value as BusinessBranch | "" })}
              >
                <option value="">Selecione</option>
                {BUSINESS_BRANCHES.map((x) => (
                  <option key={x} value={x}>
                    {BRANCH_LABEL[x]}
                  </option>
                ))}
              </select>
            </Field>
            <fieldset className="col-12 mb-3">
              <legend className="form-label fs-6">
                Atividades{" "}
                <span className="text-danger" aria-hidden="true">
                  *
                </span>
              </legend>
              <div className="row">
                {WIZARD_ACTIVITIES.map((a) => (
                  <div className="col-md-6 mb-2" key={a}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`wiz-${a}`}
                        checked={f.activities.includes(a)}
                        onChange={(e) => toggle(a, e.target.checked)}
                        aria-describedby={`wiz-${a}-hint`}
                      />
                      <label className="form-check-label" htmlFor={`wiz-${a}`}>
                        {ACTIVITY_LABEL[a].label}
                      </label>
                      <div id={`wiz-${a}-hint`} className="form-text mt-0">
                        {ACTIVITY_LABEL[a].hint}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.activities && <div className="invalid-feedback d-block">{errors.activities}</div>}
            </fieldset>
            {simples && (
              <Field
                label="Alíquota de crédito do Simples"
                className="col-md-4"
                error={errors.creditSnPct4}
                hint="Se informada, as vendas para revenda usam CSOSN 101 com crédito; vazio = CSOSN 102."
              >
                <PercentInput value={f.creditSnPct4} onChange={(v) => setF({ ...f, creditSnPct4: v })} />
              </Field>
            )}
            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={run.isPending}>
                Criar naturezas
              </button>
            </div>
          </form>
        </div>
      </section>
      {r && (
        <section className="box card mb-3" aria-labelledby="tax-wizard-result" aria-live="polite">
          <div className="card-body">
            <h2 id="tax-wizard-result" className="h5">
              Resultado
            </h2>
            <p>CRT da empresa: {CRT_LABEL[r.crt]}.</p>
            {r.created.length > 0 && (
              <>
                <h3 className="h6">Criadas</h3>
                <ul>
                  {r.created.map((c) => (
                    <li key={c.id}>
                      <Link to="/preferencias/fiscal/naturezas/$id" params={{ id: c.id }}>
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {r.skipped.length > 0 && (
              <>
                <h3 className="h6">Já existiam (não alteradas)</h3>
                <ul>
                  {r.skipped.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </>
            )}
            <Link to="/preferencias/fiscal/naturezas" className="btn btn-outline-primary">
              Ver naturezas
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
